import React, { ReactNode, useEffect, useMemo } from 'react';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Header,
  useReactTable,
} from '@tanstack/react-table';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import { ArrowLeft, ArrowRight, Download, Loader } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import {
  TableStoreContext,
  useTableStoreContext,
} from '@/components/table/contexts/TableContext.tsx';
import { useCreateTableStore } from '@/components/table/hooks/useCreateTableStore.ts';
import SearchAction from '@/components/table/components/SearchAction.tsx';
import { TableDefinitionDto } from '@/components/table/types/TableDefinitionDto.ts';
import useColumns from '@/components/table/hooks/useColumns.tsx';
import useTableData from '@/components/table/hooks/useTableData.ts';
import useTablePreferences from '@/components/table/hooks/useTablePreferences.ts';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import SortColumnAction from '@/components/table/components/SortColumnAction.tsx';
import FilterAction from '@/components/table/components/filter/FilterAction.tsx';
import ColumnVisibilityAction from '@/components/table/components/ColumnVisibilityAction.tsx';
import { FilterCriteriaForm } from '@/components/table/schemas/filterCriteriaSchema.ts';
import { QueryKey } from '@tanstack/react-query';
import TableSelectionBar from '@/components/table/components/bulkActions/TableSelectionBar.tsx';
import { BulkAction } from '@/components/table/types/BulkAction.ts';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import QuickFilters from '@/components/table/components/filter/QuickFilters.tsx';
import { exportToExcel } from '@/components/table/_export/TableExport.ts';
import TableButtonMock from '@/components/buttons/TableButtonMock.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import TruncatedCell from '@/components/table/components/TruncatedCell.tsx';
import {
  getColumnId,
  OPTIONS_COLUMN_ID,
  SELECT_COLUMN_ID,
} from '@/components/table/preferences/columnPreferences.ts';
import {
  isPrimaryField,
  resolveFieldImportance,
} from '@/components/table/preferences/fieldImportance.ts';
import { FieldImportance } from '@/components/table/types/FieldImportance.ts';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import MobileRecordList from '@/components/table/components/mobile/MobileRecordList.tsx';
import MobileRecordListSkeleton from '@/components/table/components/mobile/MobileRecordListSkeleton.tsx';
import {
  ListSearch,
  listSearchKey,
} from '@/components/table/routes/listSearchParams.ts';

export interface TableProps<T extends RowWithId> {
  queryKey: QueryKey;
  fetchData: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaForm,
  ) => Promise<PagedDto<T>>;
  fetchTableDef: () => Promise<TableDefinitionDto>;
  emptyDataComponent?: ReactNode;
  pageSize?: number;
  /**
   * Enables row selection. Pass the context actions shown in the floating
   * selection bar — an empty array still enables selection + "export selected".
   */
  bulkActions?: BulkAction<T>[];
  rowContextMenuItems?: RowContextMenuItemParams<T>[];
  actionsAndFiltersVisible?: boolean;
  /** Object type for the TAGS column — enables inline tag editing in cells. */
  tagTargetType?: TaggableType;
  /** Screen/section title rendered to the left of the toolbar — renders even when `actionsAndFiltersVisible` is false. Omit on sub-tab screens, where the title sits above the tabs instead. */
  title?: ReactNode;
  /** Single, verb-labeled primary action for the section — rendered at the end of the toolbar (after a separator when the tool cluster is also shown). Renders even when `actionsAndFiltersVisible` is false. */
  primaryAction?: ReactNode;
  /** Wycinek z adresu ekranu (`?query=`, `?quick=`, `?filters=`) — stan początkowy czytany raz; tabela nie zapisuje go z powrotem. */
  listSearch?: ListSearch;
}

/** Store czyta adres tylko przy tworzeniu — inny wycinek w adresie to nowa tabela, nie aktualizacja starej. */
const Table = <T extends RowWithId>(props: TableProps<T>) => (
  <TableWithStore key={listSearchKey(props.listSearch)} {...props} />
);

const TableWithStore = <T extends RowWithId>({
  queryKey,
  fetchData,
  fetchTableDef,
  emptyDataComponent,
  pageSize = 20,
  bulkActions,
  rowContextMenuItems,
  actionsAndFiltersVisible = true,
  tagTargetType,
  title,
  primaryAction,
  listSearch,
}: TableProps<T>) => {
  const isMobile = useIsMobile();
  const tableStore = useCreateTableStore({ pageSize, listSearch });

  // Stable key identifying this table for persisting column preferences.
  const tableKey = useMemo(() => JSON.stringify(queryKey), [queryKey]);

  return (
    <TableStoreContext.Provider value={tableStore}>
      <TableWrapper>
        {(actionsAndFiltersVisible ||
          title !== undefined ||
          primaryAction !== undefined) && (
          <div
            className={cn(
              'flex flex-row flex-wrap items-center gap-x-1.5 gap-y-2',
              title === undefined &&
                (actionsAndFiltersVisible || primaryAction !== undefined) &&
                'justify-end',
            )}
          >
            {title !== undefined && (
              <div className="mr-auto text-2xl font-extrabold tracking-[-0.01em] text-base-text">
                {title}
              </div>
            )}
            {actionsAndFiltersVisible && (
              <div className="flex flex-row flex-wrap items-center gap-1.5 max-[1100px]:order-2 max-[1100px]:basis-full">
                <SearchAction />
                <FilterAction />
                <QuickFilters />
                {/* Lista kart nie ma kolumn, którymi dałoby się sterować. */}
                {!isMobile && <ColumnVisibilityAction />}
                <ExportAction />
              </div>
            )}
            {primaryAction !== undefined && (
              <div className="flex flex-row items-center gap-1.5 max-[1100px]:order-1">
                {actionsAndFiltersVisible && (
                  <div className="h-[22px] w-px bg-neutral-200" />
                )}
                {primaryAction}
              </div>
            )}
          </div>
        )}
        <TableComponent
          queryKey={queryKey}
          tableKey={tableKey}
          fetchData={fetchData}
          fetchTableDef={fetchTableDef}
          emptyDataComponent={emptyDataComponent}
          rowContextMenuItems={rowContextMenuItems}
          allowBulkActions={bulkActions !== undefined}
          tagTargetType={tagTargetType}
          isMobile={isMobile}
        />
      </TableWrapper>
      {/* Zaznaczenia nie da się zrobić na liście kart, ale zwężenie okna
          zastałoby pasek z zaznaczeniem zrobionym jeszcze na desktopie. */}
      {!isMobile && bulkActions && (
        <TableSelectionBar bulkActions={bulkActions} />
      )}
    </TableStoreContext.Provider>
  );
};

const TableComponent = <T extends RowWithId>({
  queryKey,
  tableKey,
  fetchData,
  fetchTableDef,
  emptyDataComponent,
  allowBulkActions,
  rowContextMenuItems,
  tagTargetType,
  isMobile,
}: {
  queryKey: QueryKey;
  tableKey: string;
  fetchData: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaForm,
  ) => Promise<PagedDto<T>>;
  fetchTableDef: () => Promise<TableDefinitionDto>;
  emptyDataComponent?: ReactNode;
  allowBulkActions: boolean;
  rowContextMenuItems?: RowContextMenuItemParams<T>[];
  tagTargetType?: TaggableType;
  isMobile: boolean;
}) => {
  const { columns, isLoading: isColumnDefLoading } = useColumns<T>({
    queryKey: queryKey,
    fetchTableDef: fetchTableDef,
    allowBulkActions: allowBulkActions,
    rowContextMenuItems,
    tagTargetType,
  });

  const { data, isLoading } = useTableData({
    queryKey: queryKey,
    fetchData,
    // Lista mobilna dokłada strony własnym zapytaniem — bez tego tabela
    // wystrzeliłaby jeszcze jedno, po dane, których nikt nie rysuje.
    enabled: !isMobile,
  });

  useTablePreferences({
    tableKey,
    hasSelect: allowBulkActions,
    hasOptions: rowContextMenuItems !== undefined,
  });

  if (isMobile) {
    if (!columns || columns.length === 0) {
      // Szkielet tylko na czas ładowania definicji: pusta albo nieudana
      // definicja kolumn zostawiała na telefonie wieczny szkielet.
      return isColumnDefLoading ? (
        <MobileRecordListSkeleton />
      ) : (
        (emptyDataComponent ?? <EmptyTable />)
      );
    }
    return (
      <MobileRecordList
        queryKey={queryKey}
        fetchData={fetchData}
        columns={columns}
        rowContextMenuItems={rowContextMenuItems}
        emptyDataComponent={emptyDataComponent}
      />
    );
  }

  if (isLoading) {
    return <LoadingTable />;
  }

  // Bez definicji kolumn nie ma czego zostawić na ekranie — dopiero wtedy
  // pustka zastępuje całą tabelę. Brak samych wierszy zostaje w `tbody`, żeby
  // nagłówki powiedziały, czego właściwie nie ma.
  if (!columns || columns.length === 0) {
    return emptyDataComponent ?? <EmptyTable />;
  }

  return (
    <LoadedTable
      // Nieudane pobranie też pokazuje nagłówki: `LoadedTable` czyta z tej
      // strony wyłącznie `content` i `totalElements`.
      data={data}
      columns={columns}
      emptyDataComponent={emptyDataComponent}
    />
  );
};

const LoadedTable = <T extends RowWithId>({
  data,
  columns,
  emptyDataComponent,
}: {
  data?: PagedDto<T>;
  columns: ColumnDef<T>[];
  emptyDataComponent?: ReactNode;
}) => {
  const rows = useMemo(() => data?.content ?? [], [data?.content]);
  const pageRequest = useTableStoreContext((state) => state.pageRequest);
  const setPageRequest = useTableStoreContext((state) => state.setPageRequest);
  const rowSelection = useTableStoreContext((state) => state.rowSelection);
  const setRowSelection = useTableStoreContext(
    (state) => state.setRowSelection,
  );
  const syncSelectedRows = useTableStoreContext(
    (state) => state.syncSelectedRows,
  );
  const setExportHandler = useTableStoreContext(
    (state) => state.setExportHandler,
  );
  const tableDefinition = useTableStoreContext(
    (state) => state.tableDefinition,
  );
  const columnOrder = useTableStoreContext((state) => state.columnOrder);
  const columnVisibility = useTableStoreContext(
    (state) => state.columnVisibility,
  );
  const columnSizing = useTableStoreContext((state) => state.columnSizing);
  const setColumnOrder = useTableStoreContext((state) => state.setColumnOrder);
  const setColumnVisibility = useTableStoreContext(
    (state) => state.setColumnVisibility,
  );
  const setColumnSizing = useTableStoreContext(
    (state) => state.setColumnSizing,
  );

  const table = useReactTable<T>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: data?.totalElements ?? 0,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    onPaginationChange: (updater) => {
      const newPaginationState =
        typeof updater === 'function'
          ? updater({
              pageIndex: pageRequest.page,
              pageSize: pageRequest.size,
            })
          : updater;
      setPageRequest({
        page: newPaginationState.pageIndex,
        size: newPaginationState.pageSize,
        sortByFieldId: pageRequest.sortByFieldId,
        sortDirection: pageRequest.sortDirection,
      });
    },
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    state: {
      pagination: {
        pageIndex: pageRequest.page,
        pageSize: pageRequest.size,
      },
      rowSelection,
      columnOrder,
      columnVisibility,
      columnSizing,
    },
    // enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
  });

  // Kolumny, które użytkownik może przeciągać — bez systemowych i bez PRIMARY.
  const reorderableIdSet = useMemo(() => {
    const importance = resolveFieldImportance(tableDefinition.fields ?? []);
    const primaryIds = new Set(
      (tableDefinition.fields ?? [])
        .filter((field) => isPrimaryField(field, importance))
        .map(getColumnId),
    );
    const set = new Set<string>();
    for (const column of columns) {
      const id = column.id;
      if (
        id &&
        id !== SELECT_COLUMN_ID &&
        id !== OPTIONS_COLUMN_ID &&
        !primaryIds.has(id)
      ) {
        set.add(id);
      }
    }
    return set;
  }, [columns, tableDefinition]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (!reorderableIdSet.has(activeId) || !reorderableIdSet.has(overId)) {
      return;
    }
    setColumnOrder((old) => {
      const base =
        old.length > 0 ? old : table.getAllLeafColumns().map((c) => c.id);
      const oldIndex = base.indexOf(activeId);
      const newIndex = base.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0) return old;
      return arrayMove(base, oldIndex, newIndex);
    });
  };

  const hasRows = rows.length > 0;

  useEffect(() => {
    // Pusta tabela renderuje się teraz razem z nagłówkami, więc eksport trzeba
    // wyłączyć wprost — inaczej przycisk ożywa i oddaje arkusz bez wierszy.
    if (!hasRows) {
      setExportHandler(null);
      return;
    }
    setExportHandler(() => exportToExcel(table, tableDefinition));
    return () => setExportHandler(null);
  }, [table, tableDefinition, setExportHandler, hasRows]);

  // Capture selected rows' data by id so the selection bar stays correct
  // across page changes (independent of which pages are cached).
  useEffect(() => {
    syncSelectedRows(rows, rowSelection);
  }, [rows, rowSelection, syncSelectedRows]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleColumnDragEnd}
      >
        <TableContainer width={table.getTotalSize()}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                <SortableContext
                  items={headerGroup.headers.map((h) => h.column.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <TableHeadCell
                      key={header.id}
                      header={header}
                      reorderable={reorderableIdSet.has(header.column.id)}
                    />
                  ))}
                </SortableContext>
              </TableHeaderRow>
            ))}
          </thead>
          <tbody>
            {hasRows ? (
              table.getRowModel().rows.map((row) => (
                <TableDataRow key={row.id} selected={row.getIsSelected()}>
                  {row.getVisibleCells().map((cell) => (
                    <TableDataCell key={cell.id} cell={cell} />
                  ))}
                </TableDataRow>
              ))
            ) : (
              <TableEmptyRow colSpan={table.getVisibleLeafColumns().length}>
                {emptyDataComponent ?? <EmptyTable />}
              </TableEmptyRow>
            )}
          </tbody>
        </TableContainer>
      </DndContext>
      <TableFooter>
        <TableRowsInfo totalElements={data?.totalElements ?? 0} />
        {table.getPageCount() > 1 && (
          <div>
            <button
              className={cn('rounded-sm p-1.5 hover:bg-neutral-50', {
                'opacity-50 hover:bg-transparent': !table.getCanPreviousPage(),
              })}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ArrowLeft className="size-4 text-neutral-900" />
            </button>
            <button
              className={cn('rounded-sm p-1.5 hover:bg-neutral-50', {
                'opacity-50 hover:bg-transparent': !table.getCanNextPage(),
              })}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ArrowRight className="size-4 text-neutral-900" />
            </button>
          </div>
        )}
      </TableFooter>
    </>
  );
};

const ExportAction = () => {
  const exportHandler = useTableStoreContext((state) => state.exportHandler);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button onClick={() => exportHandler?.()}>
          <TableButtonMock iconOnly disabled={!exportHandler}>
            <Download />
          </TableButtonMock>
        </button>
      </TooltipTrigger>
      <TooltipContent>Eksportuj do XLSX</TooltipContent>
    </Tooltip>
  );
};

const LoadingTable = () => {
  return (
    <>
      <TableContainer>
        <thead>
          <TableHeaderRow>
            <th className="py-2.5">
              <Skeleton className="h-[18px] w-[60%]" />
            </th>
          </TableHeaderRow>
        </thead>
        <tbody>
          {Array(3)
            .fill(null)
            .map((_, index) => (
              <TableDataRow key={index}>
                <td className="py-2.5">
                  <Skeleton className="h-[18px] w-[70%]" />
                </td>
              </TableDataRow>
            ))}
        </tbody>
      </TableContainer>
      <TableFooter>
        <Loader className={cn('size-4 animate-spin text-neutral-900')} />
      </TableFooter>
    </>
  );
};

const TableHeaderRow = ({ children }: { children: ReactNode }) => {
  return (
    <tr className="border-b border-neutral-200 bg-neutral-50">{children}</tr>
  );
};

const TableHeadCell = <T,>({
  header,
  reorderable,
}: {
  header: Header<T, unknown>;
  reorderable: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: header.column.id, disabled: !reorderable });

  const isOptions = header.column.id === OPTIONS_COLUMN_ID;
  const isSystemColumn = isOptions || header.column.id === SELECT_COLUMN_ID;
  const alignRight = header.column.columnDef.meta?.align === 'right';

  return (
    <th
      ref={setNodeRef}
      className={cn(
        'group relative m-0 p-0 text-xs font-bold uppercase leading-[18px] tracking-[0.04em] text-neutral-700',
        alignRight ? 'text-right' : 'text-left',
        {
          'sticky right-0 !w-[28px] bg-neutral-50': isOptions,
        },
      )}
      style={{
        width: header.column.getSize(),
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : undefined,
        zIndex: isDragging ? 2 : undefined,
      }}
      colSpan={header.colSpan}
    >
      {isSystemColumn ? (
        // Zaznaczenie i menu wiersza wyśrodkowuje się na pełnej szerokości
        // komórki, tak samo jak w wierszu danych. Uchwyt przeciągania
        // i `TruncatedCell` oblepiają się wokół treści, więc przez nie
        // `justify-center` nie miało czego wyśrodkować i checkbox nagłówka
        // stał dziesięć pikseli w lewo od tych w wierszach.
        <div className="flex h-9 w-full items-center justify-center">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      ) : (
        /* `flex-row-reverse` odsuwa etykietę do prawej krawędzi i przy okazji
           przerzuca ikonę sortowania na stronę przeciwną do wyrównania. */
        <div
          className={cn(
            'relative flex h-9 w-full flex-row items-center gap-1',
            alignRight ? 'flex-row-reverse pr-2' : 'pr-1',
          )}
        >
          <div
            {...(reorderable ? { ...attributes, ...listeners } : {})}
            className={cn(
              'flex min-w-0 flex-row items-center',
              reorderable && 'cursor-grab active:cursor-grabbing',
            )}
          >
            <TruncatedCell>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </TruncatedCell>
          </div>
          <SortColumnAction fieldId={header.column.id} />
          {header.column.getCanResize() && (
            <ColumnResizeHandle header={header} />
          )}
        </div>
      )}
    </th>
  );
};

const ColumnResizeHandle = <T,>({ header }: { header: Header<T, unknown> }) => {
  return (
    <div
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'absolute right-0 top-0 flex h-full w-[7px] cursor-col-resize touch-none select-none items-center justify-center opacity-0 transition group-hover:opacity-100',
        'after:h-4 after:w-[2px] after:rounded-full after:bg-neutral-300 hover:after:bg-accent-600',
        header.column.getIsResizing() && 'opacity-100 after:bg-accent-600',
      )}
    />
  );
};

/**
 * Komunikat o braku danych jako wiersz na całą szerokość — nagłówki kolumn
 * zostają, więc widać, czego nie ma. Bez `group` i bez hoveru: to nie jest
 * rekord, w który da się kliknąć.
 */
const TableEmptyRow = ({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) => (
  <tr className="border-b border-neutral-100">
    <td colSpan={colSpan} className="px-3 py-10">
      {children}
    </td>
  </tr>
);

const TableDataRow = ({
  children,
  selected = false,
}: {
  children: ReactNode;
  selected?: boolean;
}) => {
  return (
    <tr
      className={cn('group border-b border-neutral-100 transition', {
        'bg-accent-100 hover:bg-accent-200': selected,
        'hover:bg-neutral-50': !selected,
      })}
    >
      {children}
    </tr>
  );
};

const TableDataCell = <T,>({ cell }: { cell: Cell<T, unknown> }) => {
  const isOptions = cell.column.id === OPTIONS_COLUMN_ID;
  const isSystemColumn = isOptions || cell.column.id === SELECT_COLUMN_ID;
  const isSelected = cell.row.getIsSelected();
  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
  const meta = cell.column.columnDef.meta;
  // Kolumny systemowe (zaznaczenie, menu wiersza) są wyłączone z wyrównania
  // i wytłuszczenia per typ — nie niosą wartości.
  const alignRight = !isSystemColumn && meta?.align === 'right';

  return (
    <td
      className={cn(
        'h-11 py-2 text-left text-sm font-medium leading-[18px] text-base-text',
        alignRight && 'text-right tabular-nums',
        !isSystemColumn &&
          meta?.importance === FieldImportance.PRIMARY &&
          'font-bold',
        isOptions && 'sticky right-0 py-[3px]',
        // The sticky options cell is opaque, so it must echo the row's
        // background in every state (default / hover / selected).
        isOptions &&
          (isSelected
            ? 'bg-accent-100 group-hover:bg-accent-200'
            : 'bg-basic-white group-hover:bg-neutral-50'),
      )}
      style={{ width: cell.column.getSize() }}
    >
      {isSystemColumn ? (
        <div className="whitespace-nowrap">{content}</div>
      ) : (
        <TruncatedCell className="whitespace-nowrap pr-2">
          {content}
        </TruncatedCell>
      )}
    </td>
  );
};

const TableContainer = ({
  children,
  width,
}: {
  children: ReactNode;
  width?: number;
}) => {
  return (
    <div className="relative overflow-auto">
      <table
        className={cn('min-w-[1000px] table-fixed border-collapse text-sm', {
          'w-full': width === undefined,
        })}
        style={width !== undefined ? { width } : undefined}
      >
        {children}
      </table>
    </div>
  );
};

const TableWrapper = ({ children }: { children: ReactNode }) => {
  return <div className="w-full space-y-4">{children}</div>;
};

const TableFooter = ({ children }: { children: ReactNode }) => {
  return (
    <div className="flex flex-row items-center justify-between gap-2">
      {children}
    </div>
  );
};

const TableRowsInfo = ({ totalElements }: { totalElements: number }) => {
  let rowsText = '';
  if (totalElements === 1) {
    rowsText = `${totalElements} wiersz`;
  } else if (totalElements > 1 && totalElements < 5) {
    rowsText = `${totalElements} wiersze`;
  } else {
    rowsText = `${totalElements} wierszy`;
  }
  return <div className="py-1.5 text-xs text-neutral-700">{rowsText}</div>;
};

export default Table;
